import { ReactNode } from 'react';
import { OperationalStatusOptions } from '@components/EntityDrawers/constants/common';
import { CommonKeys } from '@components/EntityDrawers/constants/keys';
import {
  EntityValidationStatus,
  Modes,
  OperationalStatus,
} from '@constants/canvas/layers';
import {
  NodeActionType,
  NodeDTO,
  NodeDTOTypes,
} from '@store/services/nodes/types';
import { CiaActionDimension } from '@utils/drawers';
import {
  CheckmarkCircleIcon,
  GlobalIcon,
  PlusCircleIcon,
  PlusIcon,
  ShieldIcon,
  SubLayerIcon,
  TimeIcon,
} from '@utils/iconsDefs';
import dayjs from 'dayjs';

export enum NodeActionNoChangeReason {
  NotEligibleType = 'not-eligible-type',
  EditingDisallowedInMode = 'editing-disallowed-in-mode',
  TimelineConflict = 'timeline-conflict',
}

export type NodeActionEligibility =
  | { eligible: true }
  | { eligible: false; reason: NodeActionNoChangeReason };

const VALIDATION_NOT_ELIGIBLE_TYPES = [
  NodeDTOTypes.Characteristic,
  NodeDTOTypes.EvaluatedRisk,
  NodeDTOTypes.Initiatives,
];
const VALIDATION_DISALLOWED_IN_RISK_MANAGEMENT = [
  NodeDTOTypes.Capabilities,
  NodeDTOTypes.Functions,
];
const OPERATIONAL_STATUS_NODE_TYPES = [
  NodeDTOTypes.Service,
  NodeDTOTypes.Capabilities,
  NodeDTOTypes.Functions,
  NodeDTOTypes.Technologies,
];

function getValidationEligibility(
  node: Pick<NodeDTO, 'type'>,
  mode: Modes,
): NodeActionEligibility {
  if (VALIDATION_NOT_ELIGIBLE_TYPES.includes(node.type)) {
    return {
      eligible: false,
      reason: NodeActionNoChangeReason.NotEligibleType,
    };
  }
  if (
    mode === Modes.RiskManagement &&
    VALIDATION_DISALLOWED_IN_RISK_MANAGEMENT.includes(node.type)
  ) {
    return {
      eligible: false,
      reason: NodeActionNoChangeReason.EditingDisallowedInMode,
    };
  }
  return { eligible: true };
}

export function getOperationalEligibility(
  node: Pick<NodeDTO, 'type'>,
  mode: Modes,
): NodeActionEligibility {
  if (!OPERATIONAL_STATUS_NODE_TYPES.includes(node.type)) {
    return {
      eligible: false,
      reason: NodeActionNoChangeReason.NotEligibleType,
    };
  }
  if (mode === Modes.RiskManagement) {
    return {
      eligible: false,
      reason: NodeActionNoChangeReason.EditingDisallowedInMode,
    };
  }
  if (mode === Modes.BusinessAlignment && node.type !== NodeDTOTypes.Service) {
    return {
      eligible: false,
      reason: NodeActionNoChangeReason.EditingDisallowedInMode,
    };
  }
  return { eligible: true };
}

const OPERATIONAL_STATUS_VALUES = Object.values(OperationalStatus) as string[];

export function hasOperationalTimelineConflict(
  node: Pick<NodeDTO, 'created_at' | 'meta_data'>,
  value: string,
): boolean {
  if (!OPERATIONAL_STATUS_VALUES.includes(value)) return false;

  const rawStartDate = (node.meta_data as { start_date?: string } | undefined)
    ?.start_date;
  if (!rawStartDate || !node.created_at) return false;

  const startDate = dayjs(rawStartDate);
  const createdAt = dayjs(node.created_at);
  if (!startDate.isValid() || !createdAt.isValid()) return false;

  return value === OperationalStatus.InDevelopment
    ? startDate.isBefore(createdAt, 'day')
    : startDate.isAfter(createdAt, 'day');
}

export interface NodeActionConfig {
  actionType: NodeActionType;
  label: string;
  menuLabel: string;
  menuText: string;
  menuToChangeTitle: string;
  menuToSkipTitle: string;
  menuConfirmName: string;
  successMessage: string;
  successMessageWithCount?: (count: number) => string;
  menuFooterText?: string;
  icon: ReactNode;
  getEligibility: (
    node: Pick<NodeDTO, 'type'>,
    mode: Modes,
  ) => NodeActionEligibility;
  statusOptions?: { value: string; label: string }[];
  keepMenuOpenOnApply?: boolean;
  readCurrent?: (
    node: Pick<NodeDTO, 'valid_status' | 'meta_data'>,
  ) => string | undefined;
  isValueBlockedForNode?: (
    node: Pick<NodeDTO, 'created_at' | 'meta_data'>,
    value: string,
  ) => boolean;
  blockedValueTooltip?: (value: string) => string;
  ciaDimension?: CiaActionDimension;
}

export const NODE_ACTIONS: Partial<Record<NodeActionType, NodeActionConfig>> = {
  [NodeActionType.ChangeValidationStatus]: {
    actionType: NodeActionType.ChangeValidationStatus,
    label: 'Set validation status',
    menuLabel: 'Set validation status?',
    menuText: 'The selected status cannot be applied to all selected entities.',
    menuToChangeTitle: 'Status will be set for',
    menuToSkipTitle: 'Status will not be set for',
    menuConfirmName: 'Set status',
    icon: <CheckmarkCircleIcon fill="currentColor" width={20} height={20} />,
    statusOptions: [
      { value: EntityValidationStatus.Draft, label: 'Draft' },
      { value: EntityValidationStatus.Validated, label: 'Validated' },
    ],
    successMessage: 'Validation status has been successfully updated.',
    keepMenuOpenOnApply: true,
    getEligibility: getValidationEligibility,
    readCurrent: (node) => node.valid_status,
  },
  [NodeActionType.ChangeOperationalStatus]: {
    actionType: NodeActionType.ChangeOperationalStatus,
    label: 'Set operational status',
    menuLabel: 'Set operational status?',
    menuText: 'The selected status cannot be applied to all selected entities.',
    menuToChangeTitle: 'Status will be set for',
    menuToSkipTitle: 'Status will not be set for',
    menuConfirmName: 'Set status',
    icon: <TimeIcon fill="currentColor" width={20} height={20} />,
    statusOptions: OperationalStatusOptions,
    successMessage: 'Operational status has been successfully updated.',
    keepMenuOpenOnApply: true,
    getEligibility: getOperationalEligibility,
    readCurrent: (node) =>
      (node.meta_data as { operational_status?: string } | undefined)
        ?.operational_status,
    isValueBlockedForNode: hasOperationalTimelineConflict,
    blockedValueTooltip: (value) =>
      value === OperationalStatus.InDevelopment
        ? 'Not available while the timeline start date is before the entity creation date. Adjust the timeline in the entity drawer.'
        : 'Not available while the timeline start date is after the entity creation date. Adjust the timeline in the entity drawer.',
  },
  [NodeActionType.AddTargetScope]: {
    actionType: NodeActionType.AddTargetScope,
    label: 'Add target scope',
    menuLabel: 'Add target scope?',
    menuText: 'Target scope cannot be applied to all selected entities.',
    menuToChangeTitle: 'Target scope will be added for',
    menuToSkipTitle: 'Target scope will not be added for',
    menuConfirmName: 'Add target scope',
    icon: <GlobalIcon fill="currentColor" width={20} height={20} />,
    successMessage: 'Target scope has been successfully added.',
    getEligibility: getTargetScopeEligibility,
  },
  [NodeActionType.AddCoverage]: {
    actionType: NodeActionType.AddCoverage,
    label: 'Add coverage',
    menuLabel: 'Add coverage?',
    menuText: 'Coverage cannot be applied to all selected entities.',
    menuToChangeTitle: 'Coverage will be added for',
    menuToSkipTitle: 'Coverage will not be added for',
    menuConfirmName: 'Add coverage',
    icon: <SubLayerIcon fill="currentColor" width={20} height={20} />,
    successMessage: 'Coverage has been successfully updated.',
    getEligibility: getOperationalEligibility,
    ciaDimension: CommonKeys.Coverage,
  },
  [NodeActionType.AddAssurance]: {
    actionType: NodeActionType.AddAssurance,
    label: 'Add assurance',
    menuLabel: 'Add assurance?',
    menuText: 'Assurance cannot be applied to all selected entities.',
    menuToChangeTitle: 'Assurance will be added for',
    menuToSkipTitle: 'Assurance will not be added for',
    menuConfirmName: 'Add assurance',
    icon: <ShieldIcon fill="currentColor" width={20} height={20} />,
    successMessage: 'Assurance has been successfully updated.',
    getEligibility: getOperationalEligibility,
    ciaDimension: CommonKeys.Assurance,
  },
  [NodeActionType.AddApplicability]: {
    actionType: NodeActionType.AddApplicability,
    label: 'Add applicability',
    menuLabel: 'Add applicability?',
    menuText: 'Applicability cannot be applied to all selected entities.',
    menuToChangeTitle: 'Applicability will be added for',
    menuToSkipTitle: 'Applicability will not be added for',
    menuConfirmName: 'Add applicability',
    icon: <PlusCircleIcon fill="currentColor" width={20} height={20} />,
    successMessage: 'Applicability has been successfully updated.',
    getEligibility: getOperationalEligibility,
    ciaDimension: CommonKeys.Applicability,
  },
  [NodeActionType.CreateRelationships]: {
    actionType: NodeActionType.CreateRelationships,
    label: 'Create relationship',
    menuLabel: 'Create relationships?',
    menuText: 'Relationships cannot be created for all selected entities.',
    menuToChangeTitle: 'Will be included',
    menuToSkipTitle: 'Will be skipped',
    menuConfirmName: 'Create relationships',
    menuFooterText:
      'Only relationships between the included entities will be created.',
    icon: <PlusIcon fill="currentColor" width={20} height={20} />,
    successMessage: 'The relationship has been successfully created.',
    successMessageWithCount: (count: number) =>
      `${count} relationships have been successfully created.`,
    getEligibility: () => ({ eligible: true }),
  },
};

export function countEligibleNodeActions(
  node: Pick<NodeDTO, 'type'>,
  mode: Modes,
): number {
  return Object.values(NODE_ACTIONS).reduce(
    (count, config) =>
      count + (config.getEligibility(node, mode).eligible ? 1 : 0),
    0,
  );
}

export function getMostCapableNode<T extends Pick<NodeDTO, 'type'>>(
  nodes: T[],
  mode: Modes,
): T | undefined {
  if (nodes.length === 0) return undefined;
  return nodes.reduce((best, node) =>
    countEligibleNodeActions(node, mode) > countEligibleNodeActions(best, mode)
      ? node
      : best,
  );
}

export enum StatusOptionUnavailableReason {
  NotApplicable = 'not-applicable',
  TimelineConflict = 'timeline-conflict',
}

export type StatusOptionAvailability =
  | { available: true }
  | { available: false; reason: StatusOptionUnavailableReason };

export function getStatusOptionAvailability(
  nodes: Pick<NodeDTO, 'type' | 'valid_status' | 'meta_data' | 'created_at'>[],
  mode: Modes,
  value: string,
  config: NodeActionConfig,
): StatusOptionAvailability {
  const candidates = nodes.filter(
    (node) =>
      config.getEligibility(node, mode).eligible &&
      config.readCurrent?.(node) !== value,
  );

  if (!candidates.length) {
    return {
      available: false,
      reason: StatusOptionUnavailableReason.NotApplicable,
    };
  }
  if (candidates.every((node) => config.isValueBlockedForNode?.(node, value))) {
    return {
      available: false,
      reason: StatusOptionUnavailableReason.TimelineConflict,
    };
  }
  return { available: true };
}

const TARGET_SCOPE_INELIGIBLE_TYPES = [
  NodeDTOTypes.Characteristic,
  NodeDTOTypes.EvaluatedRisk,
];
const TARGET_SCOPE_DISALLOWED_IN_RISK_MANAGEMENT = [
  NodeDTOTypes.Capabilities,
  NodeDTOTypes.Functions,
];

export function getTargetScopeEligibility(
  node: Pick<NodeDTO, 'type' | 'parent_asset'>,
  mode: Modes,
  assetId?: string | null,
  selectedNodeIds?: ReadonlySet<string>,
): NodeActionEligibility {
  if (TARGET_SCOPE_INELIGIBLE_TYPES.includes(node.type)) {
    const inheritsFromSelectedParent =
      !!node.parent_asset && !!selectedNodeIds?.has(node.parent_asset);
    return inheritsFromSelectedParent
      ? { eligible: true }
      : {
          eligible: false,
          reason: NodeActionNoChangeReason.NotEligibleType,
        };
  }
  if (mode === Modes.RiskManagement) {
    if (node.type === NodeDTOTypes.Initiatives) {
      return !!assetId && node.parent_asset === assetId
        ? { eligible: true }
        : {
            eligible: false,
            reason: NodeActionNoChangeReason.NotEligibleType,
          };
    }
    if (TARGET_SCOPE_DISALLOWED_IN_RISK_MANAGEMENT.includes(node.type)) {
      return {
        eligible: false,
        reason: NodeActionNoChangeReason.EditingDisallowedInMode,
      };
    }
  }
  return { eligible: true };
}

export function isTargetScopeVisible(
  nodes: Pick<NodeDTO, 'id' | 'type' | 'parent_asset'>[],
  mode: Modes,
  assetId?: string | null,
): boolean {
  const selectedNodeIds = new Set(nodes.map((node) => node.id));
  return nodes.some(
    (node) =>
      getTargetScopeEligibility(node, mode, assetId, selectedNodeIds).eligible,
  );
}

export function isCiaActionVisible(
  nodes: Pick<NodeDTO, 'type'>[],
  mode: Modes,
): boolean {
  return nodes.some((node) => getOperationalEligibility(node, mode).eligible);
}
